"use client";

import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { useRouter, useSearchParams } from "next/navigation";
import { Logo } from "@/components/ui/logo";

export default function LoginPage() {
  const { login, loginWithToken, isLoading, user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoProvider, setSsoProvider] = useState("");

  // Check for SSO token in URL (callback redirect from IdP)
  const [ssoProcessing, setSsoProcessing] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token && !ssoProcessing) {
      setSsoProcessing(true);
      const refreshToken = params.get("refresh_token");
      // Remove tokens from URL bar immediately (security: don't leave JWTs in browser history)
      window.history.replaceState({}, "", "/login");

      // Validate token via auth context — no full page reload needed
      loginWithToken(token, refreshToken || undefined).then(async (success) => {
        if (success) {
          // Check if setup wizard needs completing (same as local login)
          try {
            const setupRes = await fetch("/api/admin/setup/status");
            if (setupRes.ok) {
              const setupData = await setupRes.json();
              if (!setupData.wizard_completed) {
                router.replace("/setup");
                return;
              }
            }
          } catch {}
          router.replace("/dashboard");
        } else {
          setError("SSO authentication succeeded but session could not be established. Please try again.");
          setSsoProcessing(false);
        }
      });
      return;
    }
    const ssoError = params.get("sso_error");
    if (ssoError) {
      setError(decodeURIComponent(ssoError));
    }
  }, []);

  // Check if SSO is configured
  useEffect(() => {
    fetch("/api/auth/sso/status")
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.configured && data?.enabled) {
          setSsoEnabled(true);
          setSsoProvider(data.provider_name || "SSO");
        }
      })
      .catch(() => {});
  }, []);

  // If already logged in, don't show login form
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cl-bg flex items-center justify-center">
        <div className="text-cl-slate">Loading...</div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cl-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <a href="/" className="block mb-8 px-5">
          <Logo className="w-full" />
        </a>
        <div className="bg-white rounded-xl shadow-sm border border-cl-border p-8">
          <h1 className="text-xl font-semibold text-cl-navy text-center mb-6">
            Sign in to your account
          </h1>
          {ssoEnabled && (
            <>
              <a
                href="/api/auth/sso/login"
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-white text-cl-navy text-sm font-semibold rounded-lg border-2 border-cl-navy hover:bg-cl-navy hover:text-white transition-colors mb-4"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Sign in with {ssoProvider}
              </a>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-cl-border" />
                <span className="text-xs text-cl-lavender">or use email</span>
                <div className="flex-1 h-px bg-cl-border" />
              </div>
            </>
          )}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-cl-slate mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-cl-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cl-navy/20 focus:border-cl-navy"
                placeholder="you@firm.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-cl-slate mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-cl-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cl-navy/20 focus:border-cl-navy"
                placeholder="Enter your password"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-cl-navy text-white text-sm font-semibold rounded-lg hover:bg-cl-navy-light transition-colors disabled:opacity-50"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-cl-lavender">
          Contract Lucidity — Clarity from complexity
        </p>
      </div>
    </div>
  );
}
